/**
 * Trip API service
 * Handles trip-related endpoints
 */
import { AxiosResponse } from 'axios';
import { BaseApiService } from './baseApi';
import {
  ME,
  TRIPS,
  SHARE,
  SHARED,
  ENABLE,
  REVOKE,
  ROTATE,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '../../constants';
import {
  TripRequest,
  TripDetail,
  TripSummary,
  PagedResponse,
} from '../../types';
import { ServiceResult, ApiResponse } from '../../types/common/api.types';

export class TripApiService extends BaseApiService {
  async getPublicTrips(
    page = DEFAULT_PAGE_NUMBER,
    size = DEFAULT_PAGE_SIZE
  ): Promise<ServiceResult<PagedResponse<TripSummary>>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<PagedResponse<TripSummary>> =
        await this.api.get(`${TRIPS}?page=${page}&size=${size}`);
      return response.data;
    });
  }

  async getCurrentUserTrips(
    page = DEFAULT_PAGE_NUMBER,
    size = DEFAULT_PAGE_SIZE
  ): Promise<ServiceResult<PagedResponse<TripDetail>>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<PagedResponse<TripDetail>> =
        await this.api.get(`${TRIPS}${ME}?page=${page}&size=${size}`);
      return response.data;
    });
  }

  async getTripById(id: string): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.get(
        `${TRIPS}/${id}`
      );
      return response.data;
    });
  }

  async getSharedTrip(shareToken: string): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.get(
        `${TRIPS}${SHARED}/${shareToken}`
      );
      return response.data;
    });
  }

  async createTrip(tripData: TripRequest): Promise<ServiceResult<ApiResponse>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<ApiResponse> = await this.api.post(
        `${TRIPS}`,
        tripData
      );
      return response.data;
    });
  }

  async replaceTrip(
    id: string,
    tripData: TripRequest
  ): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.put(
        `${TRIPS}/${id}`,
        tripData
      );
      return response.data;
    });
  }

  async updateTrip(
    id: string,
    tripData: Partial<TripRequest>
  ): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.patch(
        `${TRIPS}/${id}`,
        tripData
      );
      return response.data;
    });
  }

  async enableShareLink(id: string): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.post(
        `${TRIPS}/${id}${SHARE}${ENABLE}`
      );
      return response.data;
    });
  }

  async revokeShareLink(id: string): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.post(
        `${TRIPS}/${id}${SHARE}${REVOKE}`
      );
      return response.data;
    });
  }

  async rotateShareLink(id: string): Promise<ServiceResult<TripDetail>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<TripDetail> = await this.api.post(
        `${TRIPS}/${id}${SHARE}${ROTATE}`
      );
      return response.data;
    });
  }

  async deleteTrip(id: string): Promise<ServiceResult<ApiResponse>> {
    return this.withErrorHandling(async () => {
      const response: AxiosResponse<ApiResponse> = await this.api.delete(
        `${TRIPS}/${id}`
      );
      return response.data;
    });
  }
}
